import type { LucideIcon } from "lucide-react"
import { BarChart3, HardHat, Sun, Warehouse } from "lucide-react"

/**
 * Áreas da app — espelho de `functions/lib/areas.ts` com o que a UI precisa
 * (nome, ícone, rota base). O acesso resolve-se no servidor (`/api/me` →
 * `areas`) e volta a ser verificado em cada função da área.
 */
export type AreaKey = "simulador" | "producao" | "armazem" | "reporting"

export interface AreaUi {
  key: AreaKey
  label: string
  description: string
  /** Rota base; o mosaico e o botão de grelha levam aqui. */
  path: string
  icon: LucideIcon
  /** Cor do mosaico (fundo do ícone). */
  tone: string
  enabled: boolean
}

export const AREAS: readonly AreaUi[] = [
  {
    key: "simulador",
    label: "Simulador",
    description: "Propostas solar, AC e bombas de calor",
    path: "/simulador",
    icon: Sun,
    tone: "bg-amber-500",
    enabled: true,
  },
  {
    key: "producao",
    label: "Produção",
    description: "Obras, equipas e planeamento",
    path: "/producao",
    icon: HardHat,
    tone: "bg-blue-600",
    enabled: true,
  },
  {
    key: "armazem",
    label: "Armazém",
    description: "Receção de material e séries",
    path: "/armazem",
    icon: Warehouse,
    tone: "bg-emerald-600",
    enabled: true,
  },
  {
    key: "reporting",
    label: "Relatórios",
    description: "Propostas por mês, vendedor, estado e origem",
    path: "/relatorios",
    icon: BarChart3,
    tone: "bg-violet-600",
    enabled: true,
  },
]

export function areaByKey(key: AreaKey): AreaUi | undefined {
  return AREAS.find((a) => a.key === key)
}

/**
 * Área de um caminho. As rotas antigas do simulador (`/solar/:id`, `/list`,
 * `/configuracoes`, …) não mudaram de sítio e contam como Simulador; `/` (e o
 * `index.html` do iframe) é o menu → null.
 */
export function areaOfPath(pathname: string): AreaKey | null {
  const root = pathname.split("/")[1] ?? ""
  // `/contracts/<id>` é o «Abrir na app» da ficha do contrato no Koortex: abre a obra.
  if (root === "producao" || root === "contracts") return "producao"
  if (root === "armazem") return "armazem"
  if (root === "relatorios") return "reporting"
  return KNOWN_ROUTE_ROOTS.has(root) ? "simulador" : null
}

/** Primeiros segmentos de rota que a app conhece (para deep links do host). */
export const KNOWN_ROUTE_ROOTS = new Set([
  "relatorios",
  "simulador",
  "list",
  "configuracoes",
  "mapeamentos",
  "ajuda",
  "solar",
  "ac",
  "bc-aqs",
  "bc-aq",
  "producao",
  "contracts",
  "armazem",
])
